﻿import { prepare, layout, prepareWithSegments, layoutWithLines, type PrepareOptions } from '@chenglou/pretext';
import { svgToHtml } from '@/components/text-editor/svg-to-html';

interface EditorElement extends HTMLElement {
    contentEditable: string;
    innerHTML: string;
}

interface RichTextLayoutLine {
    index: number;
    text: string;
    width: number;
    x: number;
    y: number;
    baselineY: number;
}

interface RichTextLayoutResult {
    text: string;
    font: string;
    maxWidth: number;
    maxLineWidth: number;
    lineHeight: number;
    lineCount: number;
    height: number;
    textAlign: string;
    svg: {
        textAnchor: 'start' | 'middle' | 'end';
        width: number;
        height: number;
        lines: RichTextLayoutLine[];
    };
}

interface SanitizeOptions {
    removeScripts?: boolean;
    removeStyles?: boolean;
    removeEventHandlers?: boolean;
}

interface FontChange {
    fontFamily: string;
    fontStyle: string;
    timestamp: number;
}

const PRETEXT_CACHE_MAX_SIZE = 64;

const RichTextEditor = {
    fontChangeHistory: new Map<string, FontChange[]>(),
    findMatches: [] as Range[],
    currentMatchIndex: -1,
    findTerm: '',
    commitTimer: null as ReturnType<typeof setTimeout> | null,
    savedSelection: null as { startContainer: Node; startOffset: number; endContainer: Node; endOffset: number } | null,
    pretextPreparedCache: new Map<string, ReturnType<typeof prepareWithSegments>>(),

    /**
     * Execute a formatting command.
     * NOTE: execCommand is deprecated. These calls are isolated here intentionally
     * so they can be replaced with a modern alternative in one place.
     * @deprecated execCommand is a legacy API — migrate to InputEvent/insertText when browser support allows.
     */
    execLegacyCommand(command: string, value?: string | null): void {
        document.execCommand(command, false, value ?? null);
    },

    /**
     * Toggle a text formatting command (underline, strikethrough etc)
     */
    toggleFormat(editor: EditorElement | null, format: string): void {
        if (!editor) return;

        try {
            this.focus(editor);
            this.execLegacyCommand(format);
        } catch (e) {
            console.warn(`Format not supported: ${format}`, e);
        }
    },

    /**
     * Execute a generic editor command with optional value
     */
    executeCommand(editor: EditorElement | null, command: string, value?: string | null): void {
        if (!editor) { 
            return; 
        }

        try {
            this.focus(editor);
            this.execLegacyCommand(command, value || null);
        } catch (e) {
            console.warn(`Command not supported: ${command}`, e);
        }
    },

/**
     * Resolves after the browser has finished inserting natively pasted content, so .NET can safely
     * read innerHTML. Sanitization (dangerous tags, event handlers, negative text-indent) is done in C#.
     */
    waitForPasteInsertion(editor: EditorElement | null): Promise<void> {
        return new Promise<void>((resolve) => {
            if (!editor) {
                resolve();
                return;
            }

            setTimeout(resolve, 0);
        });
    },

    /**
     * Reads the plain-text clipboard entry. Returns null if access is denied or the clipboard is empty.
     * List-marker stripping is done in C# (RichTextEditor.StripListMarkers).
     */
    async readClipboardText(): Promise<string | null> {
        try {
            const text = await navigator.clipboard.readText();
            return text || null;
        } catch (e) {
            console.warn('Clipboard read denied or unavailable for plain text paste', e);
            return null;
        }
    },

    /**
     * Inserts plain text at the current selection. It replaces any active selection and takes on
     * the formatting of the surrounding text, like a native "paste and match style".
     */
    insertPlainText(editor: EditorElement | null, text: string): void {
        if (!editor || !text) {
            return;
        }

        try {
            this.focus(editor);
            this.execLegacyCommand('insertText', text);
            console.log(`Pasted ${text.length} character(s) as plain text (formatting stripped)`);
        } catch (e) {
            console.warn('Insert plain text failed', e);
        }
    },

    /**
     * Attaches a native keydown listener directly to the editor element to intercept Ctrl+Shift+V
     * (Cmd+Shift+V on macOS) before the browser's own built-in "Paste and Match Style" command runs.
     *
     * Chromium (and other browsers) bind this exact key combination internally, inside any
     * contenteditable region, to a native command that strips fonts/colours but deliberately KEEPS
     * structural formatting such as bullet/numbered lists. That native command is resolved purely from
     * whether the keydown event's default was prevented by the time dispatch finishes — a Blazor
     * @onkeydown handler cannot call preventDefault() in time because it round-trips through .NET
     * asynchronously, so the browser's native paste always wins the race and the list formatting survives.
     * Only a synchronous preventDefault(), called from a listener attached directly to the DOM element,
     * can reliably suppress it.
     */
    attachPasteWithoutFormattingGuard(editor: EditorElement | null, dotNetRef: any): void {
        if (!editor) {
            console.warn('Editor is missing');
            return;
        }

        if ((editor as any)._pasteWithoutFormattingGuardAttached) {
            return;
        }

        editor.addEventListener('keydown', (ev: KeyboardEvent) => {
            const key = ev.key?.toLowerCase();
            const isPasteWithoutFormattingShortcut = (ev.ctrlKey || ev.metaKey) && ev.shiftKey && key === 'v';

            if (!isPasteWithoutFormattingShortcut) {
                return;
            }

            // Must run synchronously, before this handler returns, to suppress the browser's native
            // "Paste and Match Style" default action — otherwise it still runs and keeps list formatting.
            ev.preventDefault();
            ev.stopPropagation();

            dotNetRef.invokeMethodAsync('OnPasteWithoutFormattingRequested');
        });

        (editor as any)._pasteWithoutFormattingGuardAttached = true;
    },

    /**
     * Replaces every <span> element inside the given root with a <tspan>,
     * preserving all attributes (including inline styles) and child nodes.
     * Operates bottom-up (deepest first) so that nested spans are converted
     * before their ancestors, keeping the DOM structure intact throughout.
     */
    convertSpansToTSpans(root: HTMLElement): void {
        try {
            // querySelectorAll returns elements in document order (top-down).
            // Reversing gives us bottom-up processing so inner spans are replaced first.
            const spans = Array.from(root.querySelectorAll('span')).reverse();

            spans.forEach((span) => {
                const tspan = document.createElement('tspan');

                // Copy all attributes from the original span
                Array.from(span.attributes).forEach((attr) => {
                    tspan.setAttribute(attr.name, attr.value);
                });

                // Move all child nodes into the tspan
                while (span.firstChild) {
                    tspan.appendChild(span.firstChild);
                }

                span.parentNode?.replaceChild(tspan, span);
            });

            console.log(`Converted ${spans.length} span(s) to tspan`);
        } catch (e) {
            console.warn('Error converting spans to tspans', e);
        }
    },

    /**
     * Move the cursor to the end of the last text node inside the editor.
     * Uses a depth-first search from the end of the tree so the cursor lands
     * inside the innermost element (e.g. a nested span) rather than at the
     * editor element boundary, which would break subsequent input.
     */
    moveCursorToEnd(editor: EditorElement | null): void {
        if (!editor) return;

        try {
            editor.focus();

            const selection = window.getSelection();
            if (!selection) return;

            const range = document.createRange();
            const lastTextNode = this._getLastTextNode(editor);

            if (lastTextNode) {
                range.setStart(lastTextNode, lastTextNode.length);
                range.collapse(true);
            } else {
                // No text nodes — fall back to the end of the editor element
                range.selectNodeContents(editor);
                range.collapse(false);
            }

            selection.removeAllRanges();
            selection.addRange(range);
        } catch (e) {
            console.warn('Failed to move cursor to end', e);
        }
    },

    /**
     * Recursively finds the last non-empty text node within a given node,
     * searching from the last child backwards so the deepest trailing text node is returned.
     */
    _getLastTextNode(node: Node): Text | null {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
            const child = node.childNodes[i];

            if (child.nodeType === Node.TEXT_NODE && (child.textContent?.length ?? 0) > 0) {
                return child as Text;
            }

            const result = this._getLastTextNode(child);
            if (result) return result;
        }

        return null;
    },

    /**
     * Get the current HTML content of the editor
     */
    getHtml(editor: EditorElement | null): string {
        return editor?.innerHTML ?? '';
    },

    /**
     * Set the HTML content of the editor directly.
     * This method does not parse or sanitize the HTML, so it should only be used
     * with trusted content. For untrusted content, use setContent instead.
     */
    setHtml(editor: EditorElement | null, html: string): void {
        if (!editor) {
            return;
        }

        editor.innerHTML = html;
    },

    /**
     * Set the HTML content of the editor.
     * Parses the supplied SVG markup string into a live SVGTextElement — preserving
     * all nested <span> children and their attributes — then delegates to svgToHtml
     * to convert the SVG structure into editor-compatible HTML.  Any <span> elements
     * produced by svgToHtml are then converted to <span> elements so the editor's
     * internal DOM remains consistent with every other method in this object.
     */
    setContent(editor: EditorElement | null, html: string): void {
        if (!editor) {
            return;
        }

        const svgElement = this._parseSvgTextElement(html);

        if (!svgElement) {
            console.warn('setContent: failed to parse SVG text element, falling back to raw innerHTML');
            editor.innerHTML = html;
            return;
        }

        const result = svgToHtml(svgElement, true);

        if (!result?.text) {
            console.warn('setContent: svgToHtml returned no content, falling back to raw innerHTML');
            editor.innerHTML = html;
            return;
        }

        editor.innerHTML = result.text;
    },

    /**
     * Parses a raw SVG markup string into a detached SVGTextElement, preserving
     * all nested child elements (tspan, etc.) and their attributes.
     *
     * Unlike stringToSvgTextElement — which sets textContent and therefore discards
     * all child nodes — this method inserts the markup into a temporary <svg>
     * container via innerHTML so the browser's SVG parser keeps the full tree intact.
     * The resulting <text> element is then detached and returned.
     *
     * Returns null when no <text> element can be found in the parsed markup.
     */
    _parseSvgTextElement(markup: string): SVGTextElement | null {
        if (!markup) {
            console.warn('_parseSvgTextElement: markup is null or empty');
            return null;
        }

        try {
            const svgNs = 'http://www.w3.org/2000/svg';
            const container = document.createElementNS(svgNs, 'svg') as SVGSVGElement;

            // Using innerHTML on an SVG element ensures the browser parses the
            // content in the SVG namespace, keeping tspan children and all attributes.
            container.innerHTML = markup;

            const textEl = container.querySelector('text') as SVGTextElement | null;

            if (!textEl) {
                console.warn('_parseSvgTextElement: no <text> element found in markup');
                return null;
            }

            // Detach from the temporary container before returning.
            container.removeChild(textEl);
            return textEl;
        } catch (e) {
            console.error('_parseSvgTextElement: failed to parse SVG markup', e);
            return null;
        }
    },

    /**
     * Inserts a new top-level paragraph (a <p> containing a <br>) at the end of the editor.
     * The cursor is moved into the new paragraph after insertion.
     *
     * The paragraph is appended as a direct child of the editor div so it stays at the
     * top level regardless of any nested span structure that may exist inside the editor.
     */
    insertParagraph(editor: EditorElement | null): void {
        if (!editor) {
            console.warn('Editor is missing');
            return;
        }

        try {
            const paragraph = document.createElement('p');
            // A <br> makes the empty p occupy line height so the cursor is visible
            const br = document.createElement('br');
            paragraph.appendChild(br);

            editor.appendChild(paragraph);

            // Move the cursor into the new paragraph
            const selection = window.getSelection();
            if (selection) {
                const range = document.createRange();
                range.setStart(paragraph, 0);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
            }

            editor.focus();
            console.log('Inserted new paragraph at top level of editor');
        } catch (e) {
            console.warn('Failed to insert paragraph', e);
        }
    },

    /**
     * Sanitize HTML by removing dangerous tags and attributes
     */
    sanitizeHtml(html: string, options: SanitizeOptions = {}): string {
        if (!html) return '';

        const {
            removeScripts = true,
            removeStyles = true,
            removeEventHandlers = true,
        } = options;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        if (removeScripts) {
            const scripts = tempDiv.querySelectorAll('script');
            scripts.forEach((script) => script.remove());
        }

        if (removeStyles) {
            const styles = tempDiv.querySelectorAll('style');
            styles.forEach((style) => style.remove());
        }

        if (removeEventHandlers) {
            const allElements = tempDiv.querySelectorAll('*');
            allElements.forEach((el) => {
                const attrs = Array.from(el.attributes);
                attrs.forEach((attr) => {
                    if (attr.name.startsWith('on')) {
                        el.removeAttribute(attr.name);
                    }
                });
            });
        }

        return tempDiv.innerHTML;
    },

    /**
     * Check if a format is currently applied to the selection
     */
    isFormatActive(format: string): boolean {
        try {
            return document.queryCommandState(format);
        } catch (e) {
            console.warn(`Cannot query format state: ${format}`, e);
            return false;
        }
    },

    /**
     * Get the text content of the editor
     */
    getTextContent(editor: EditorElement | null): string {
        return editor?.textContent || '';
    },

    /**
     * Get the current selection as a string
     */
    getSelectedText(): string {
        const selection = window.getSelection();
        return selection?.toString() ?? '';
    },

    /**
     * Clear all editor content
     */
    clear(editor: EditorElement | null): void {
        if (editor) {
            editor.innerHTML = '';
        }
    },

    /**
     * Focus the editor
     */
    focus(editor: EditorElement | null): void {
        editor?.focus();
    },

    /**
     * Check if all content in the editor is selected
     */
    isAllContentSelected(editor: EditorElement, range: Range): boolean {
        try {
            // Attempt 1: Compare text content length
            const editorText = this.getTextContent(editor);
            const selectedText = range.toString();
            
            if (editorText === selectedText) {
                console.log('All content selected (text comparison)');
                return true;
            }

            // Attempt 2: Check if range encompasses the entire editor using boundary comparison
            const fullRange = document.createRange();
            fullRange.selectNodeContents(editor);

            // Check start position
            const startComparison = range.compareBoundaryPoints(Range.START_TO_START, fullRange);
            const endComparison = range.compareBoundaryPoints(Range.END_TO_END, fullRange);

            // Start should be at or before the editor start (0 or no offset)
            // End should be at or after the editor end
            const isFullSelection = startComparison <= 0 && endComparison >= 0;

            if (isFullSelection) {
                console.log('All content selected (boundary comparison)');
                return true;
            }

            // Attempt 3: Check if range common ancestor is editor and spans entire content
            const commonAncestor = range.commonAncestorContainer;
            const rangeStart = range.startContainer;
            const rangeEnd = range.endContainer;

            // Get the first and last nodes in the editor
            let firstNode = editor.firstChild;
            let lastNode = editor.lastChild;

            // Skip empty text nodes
            while (firstNode && firstNode.nodeType === Node.TEXT_NODE && !firstNode.textContent?.trim()) {
                firstNode = firstNode.nextSibling;
            }
            while (lastNode && lastNode.nodeType === Node.TEXT_NODE && !lastNode.textContent?.trim()) {
                lastNode = lastNode.previousSibling;
            }

            // If range starts at the first node and ends at the last node
            if (firstNode && lastNode) {
                const startsAtBeginning = 
                    (rangeStart === firstNode && range.startOffset === 0) ||
                    (rangeStart === editor && range.startOffset === 0);
                
                const endsAtEnd = 
                    (rangeEnd === lastNode && range.endOffset === (lastNode.textContent?.length || 0)) ||
                    (rangeEnd === editor && range.endOffset === editor.childNodes.length);

                if (startsAtBeginning && endsAtEnd) {
                    console.log('All content selected (node boundary)');
                    return true;
                }
            }

            console.log('Partial content selected', {
                editorText: editorText.substring(0, 50),
                selectedText: selectedText.substring(0, 50),
                startComparison,
                endComparison
            });

            return false;
        } catch (e) {
            console.warn('Error checking if all content is selected', e);
            return false;
        }
    },

    /**
     * Check if any content in the editor is selected (selection is not empty)
     */
    isAnyContentSelected(editor: EditorElement | null): boolean {
        try {
            const selection = window.getSelection();

            // No selection object or empty range count
            if (!selection || selection.rangeCount === 0) {
                console.log('No selection object or empty range count');
                return false;
            }

            const range = selection.getRangeAt(0);

            // Selection is collapsed (cursor position, not a range)
            if (range.collapsed) {
                console.log('Selection is collapsed (no content selected)');
                return false;
            }

            // Has content selected
            const selectedText = range.toString();
            if (selectedText.length === 0) {
                console.log('Selected text is empty');
                return false;
            }

            console.log('Content is selected:', selectedText.substring(0, 50));
            return true;
        } catch (e) {
            console.warn('Error checking if any content is selected', e);
            return false;
        }
    },

    /**
     * Remove all font family spans from the editor and return the plain content
     */
    stripFontFamilySpans(editor: EditorElement): void {
        try {
            // Find all spans with font-family style
            const fontSpans = editor.querySelectorAll('span[style*="font-family"]');

            fontSpans.forEach((span) => {
                // Move all children out of the span
                while (span.firstChild) {
                    span.parentNode?.insertBefore(span.firstChild, span);
                }
                // Remove the now-empty span
                span.remove();
            });

            console.log(`Removed ${fontSpans.length} font family spans`);
        } catch (e) {
            console.warn('Error stripping font family spans', e);
        }
    },

    /**
     * Replace all font family changes with a new uniform font.
     * When <p> elements are present the wrapper span is applied per-paragraph
     * so that block structure is preserved and span never wraps across <p> boundaries.
     */
    replaceAllFontFamilySpans(editor: EditorElement, fontFamily: string, fontStyle: string, fontSize?: number): void {
        try {
            // Strip all existing font-family spans first.
            this.stripFontFamilySpans(editor);

            const buildStyleString = (): string => {
                let s = `font-family: ${fontFamily};`;
                if (fontStyle && fontStyle.toLowerCase() !== 'regular') {
                    const lower = fontStyle.toLowerCase();
                    if (lower.includes('bold')) s += ' font-weight: bold;';
                    if (lower.includes('italic')) s += ' font-style: italic;';
                }
                if (fontSize && fontSize > 0) {
                    s += ` font-size: ${fontSize}pt;`;
                }
                return s;
            };

            const paragraphs = editor.querySelectorAll('p');

            if (paragraphs.length > 0) {
                // Wrap each paragraph's contents in a styled span independently so the span never crosses a <p> boundary.
                paragraphs.forEach((p) => {
                    const newSpan = document.createElement('span');
                    newSpan.setAttribute('style', buildStyleString());

                    while (p.firstChild) {
                        newSpan.appendChild(p.firstChild);
                    }

                    p.appendChild(newSpan);
                });
            } else {
                // No <p> elements — original flat behaviour.
                const newSpan = document.createElement('span');
                newSpan.setAttribute('style', buildStyleString());

                while (editor.firstChild) {
                    newSpan.appendChild(editor.firstChild);
                }

                editor.appendChild(newSpan);
            }

            // Re-select all content.
            const selection = window.getSelection();
            if (selection) {
                const range = document.createRange();
                range.selectNodeContents(editor);
                selection.removeAllRanges();
                selection.addRange(range);
            }

            console.log(`Replaced all font families with: ${fontFamily} ${fontStyle}${fontSize ? ` ${fontSize}pt` : ''}`);
        } catch (e) {
            console.warn('Error replacing all font family spans', e);
        }
    },

    /**
       * Apply font only to the selected portion.
       * editor is required so that cross-paragraph selections can be split at <p> boundaries.
       */
    applyFontToSelection(editor: EditorElement, range: Range, fontFamily: string, fontStyle: string, fontSize?: number): void {
        try {
            let styleString = `font-family: "${fontFamily}" !important;`;

            if (fontStyle && fontStyle.toLowerCase() !== 'regular') {
                const lowerStyle = fontStyle.toLowerCase();
                if (lowerStyle.includes('bold')) {
                    styleString += ' font-weight: bold !important;';
                }
                if (lowerStyle.includes('italic')) {
                    styleString += ' font-style: italic !important;';
                }
            }

            if (fontSize && fontSize > 0) {
                styleString += ` font-size: ${fontSize}pt !important;`;
            }

            this._applyWrapperToRange(range, editor, () => {
                const span = document.createElement('span');
                span.setAttribute('style', styleString);
                return span;
            });

            console.log(`Applied font to selection: ${fontFamily} ${fontStyle}${fontSize ? ` ${fontSize}pt` : ''}`);
            console.log(`Style string: ${styleString}`);
        } catch (e) {
            console.warn('Error applying font to selection', e);
        }
    },

    /**
     * Track font family change
     */
    trackFontChange(editorId: string, fontFamily: string, fontStyle: string): void {
        if (!this.fontChangeHistory.has(editorId)) {
            this.fontChangeHistory.set(editorId, []);
        }

        const changes = this.fontChangeHistory.get(editorId);
        if (changes) {
            changes.push({
                fontFamily,
                fontStyle,
                timestamp: Date.now()
            });
        }
    },

    /**
    * Apply font family, style and size directly to current selection.
    * If entire text is selected, replaces all previous font family changes.
    * Otherwise, applies only to the selected text.
    */
    applyFontFamilyStyleAndSize(editor: EditorElement | null, fontFamily: string, fontStyle: string, fontSize?: number): boolean {
        if (!editor || !fontFamily) {
            console.warn('Editor or fontFamily is missing');
            return false;
        }

        try {
            if (!this.isAnyContentSelected(editor)) {
                return this.insertStyledSpanAtCursor(editor, fontFamily, fontStyle, fontSize);
            }

            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0) {
                console.warn('No text selected');
                return false;
            }

            const range = selection.getRangeAt(0);

            if (range.collapsed) {
                console.warn('No text selected for font change');
                return false;
            }

            const editorId = editor.id || 'default-editor';
            const isAllSelected = this.isAllContentSelected(editor, range);

            if (isAllSelected) {
                console.log('All content selected - replacing all font family and size changes');
                this.replaceAllFontFamilySpans(editor, fontFamily, fontStyle, fontSize);
            } else {
                console.log('Partial selection - applying font and size to selection only');
                // Pass editor so _applyWrapperToRange can split at <p> boundaries.
                this.applyFontToSelection(editor, range, fontFamily, fontStyle, fontSize);
            }

            this.trackFontChange(editorId, fontFamily, fontStyle);

            console.log(`Applied font: ${fontFamily} ${fontStyle}${fontSize ? ` ${fontSize}pt` : ''}, All selected: ${isAllSelected}`);
            return true;
        } catch (e) {
            console.warn(`Font application failed: ${fontFamily} ${fontStyle}${fontSize ? ` ${fontSize}pt` : ''}`, e);
            return false;
        }
    },

    /**
     * Create a new span with specified styles at the current cursor position
     */
    insertStyledSpanAtCursor(editor: EditorElement | null, fontFamily: string, fontStyle: string, fontSize?: number): boolean {
        if (!editor || !fontFamily) {
            console.warn('Editor or fontFamily is missing');
            return false;
        }

        try {
            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0) {
                console.warn('No selection available');
                return false;
            }

            // Make sure the insertion point is inside the editor, not the toolbar.
            let range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

            if (!range || !editor.contains(range.commonAncestorContainer)) {
                editor.focus();
                range = document.createRange();

                if (this.savedSelection && editor.contains(this.savedSelection.startContainer)) {
                    range.setStart(this.savedSelection.startContainer, this.savedSelection.startOffset);
                    range.setEnd(this.savedSelection.endContainer, this.savedSelection.endOffset);
                } else {
                    const lastText = this._getLastTextNode(editor);
                    if (lastText) {
                        range.setStart(lastText, lastText.length);
                    } else {
                        range.selectNodeContents(editor);
                    }
                }

                selection.removeAllRanges();
                selection.addRange(range);
            }

            // Collapse the range to cursor position (removes any selection)
            range.collapse(false);

            // Create a new span with the specified styles
            const newSpan = document.createElement('span');
            newSpan.contentEditable = 'true';

            // Build the complete style string and wrap font family in quotes to handle spaces and special characters
            let styleString = `font-family: "${fontFamily}" !important;`;

            // Add font weight
            if (fontStyle && fontStyle.toLowerCase() !== 'regular') {
                const lowerStyle = fontStyle.toLowerCase();
                if (lowerStyle.includes('bold')) {
                    styleString += ' font-weight: bold !important;';
                }
                if (lowerStyle.includes('italic')) {
                    styleString += ' font-style: italic !important;';
                }
            }

            // Apply font size if provided
            if (fontSize && fontSize > 0) {
                styleString += ` font-size: ${fontSize}pt !important;`;
            }

            // Apply all styles at once
            newSpan.setAttribute('style', styleString);

            // Add a zero-width space to make the span selectable and typeable
            const textNode = document.createTextNode('\u200B');
            newSpan.appendChild(textNode);

            // Insert the span at the cursor position
            range.insertNode(newSpan);

            // Move the cursor inside the new span after the zero-width space
            const newRange = document.createRange();
            newRange.setStart(textNode, 1);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);

            // Verify the font was applied
            const computedStyle = window.getComputedStyle(newSpan);
            const appliedFont = computedStyle.fontFamily;
            console.log(`Created new styled span with font: ${fontFamily} ${fontStyle}${fontSize ? ` ${fontSize}pt` : ''}`);
            console.log(`Style string: ${styleString}`);
            console.log(`Computed font family: ${appliedFont}`);
            
            if (!appliedFont.includes(fontFamily)) {
                console.error(`Font family NOT applied correctly! Expected: ${fontFamily}, Got: ${appliedFont}`);
            }

            return true;
        } catch (e) {
            console.warn(`Failed to insert styled span: ${fontFamily} ${fontStyle}${fontSize ? ` ${fontSize}pt` : ''}`, e);
            return false;
        }
    },

    /**
     * Get font change history for an editor
     */
    getFontChangeHistory(editorId: string): FontChange[] {
        return this.fontChangeHistory.get(editorId) ?? [];
    },

    /**
     * Clear font change history for an editor
     */
    clearFontChangeHistory(editorId: string): void {
        this.fontChangeHistory.delete(editorId);
    },

    /**
     * Ensure the cursor is positioned in the deepest nested span
     * Called on click to maintain proper cursor positioning
     */
    ensureCursorInDeepestSpan(editor: EditorElement | null): boolean {
        if (!editor) {
            console.warn('Editor is missing');
            return false;
        }

        try {
            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0) {
                console.warn('No selection available');
                return false;
            }

            const range = selection.getRangeAt(0);
            const cursorOffset = range.endOffset;
            let currentNode: Node = range.endContainer;

            // If it's a text node, get its parent element
            if (currentNode.nodeType === Node.TEXT_NODE) {
                currentNode = currentNode.parentElement!;
            }

            // Find the deepest span starting from the editor
            const deepestSpan = this._findDeepestSpan(editor);

            if (!deepestSpan) {
                console.log('No span found in editor');
                return false;
            }

            // Check if deepest span contains only a zero-width space
            const textContent = deepestSpan.textContent ?? '';
            const trimmedContent = textContent.trim();
            const containsOnlyZeroWidthSpace = textContent === '\u200B' || trimmedContent === '';

            console.log('Deepest span text content check:', {
                textContent: textContent,
                length: textContent.length,
                charCode: textContent.charCodeAt(0),
                containsOnlyZeroWidthSpace: containsOnlyZeroWidthSpace
            });
            
            if (containsOnlyZeroWidthSpace) {
                console.log('Deepest span contains only zero-width space or is empty');
                
                // Position cursor after the zero-width space if it exists
                if (deepestSpan.firstChild && deepestSpan.firstChild.nodeType === Node.TEXT_NODE) {
                    const textNode = deepestSpan.firstChild as Text;
                    const textLength = textNode.textContent?.length || 0;
                    const cursorPosition = textLength > 0 ? textLength : 0;
                    
                    const newRange = document.createRange();
                    newRange.setStart(textNode, cursorPosition);
                    newRange.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                    
                    console.log(`Cursor positioned at offset ${cursorPosition} in deepest span`);
                    return true;
                } else {
                    // No text node, position at start of span
                    const newRange = document.createRange();
                    newRange.setStart(deepestSpan, 0);
                    newRange.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                    
                    console.log('Cursor positioned at start of empty deepest span');
                    return true;
                }
            }

            console.log('Cursor moved to deepest span');
            return true;
        } catch (e) {
            console.warn('Failed to ensure cursor in deepest span', e);
            return false;
        }
    },

    /**
     * Find the deepest nested span in the editor
     */
    _findDeepestSpan(editor: EditorElement): HTMLElement | null {
        try {
            // Find all spans in the editor
            const allSpans = editor.querySelectorAll('span');
            
            if (allSpans.length === 0) {
                return null;
            }

            let deepestSpan: HTMLElement | null = null;
            let maxDepth = -1;

            // Check each span to find the deepest one
            allSpans.forEach((span) => {
                let depth = 0;
                let current = span.parentElement;

                // Count how many span ancestors this element has
                while (current && current !== editor) {
                    if (current.tagName.toLowerCase() === 'span') {
                        depth++;
                    }
                    current = current.parentElement;
                }

                if (depth > maxDepth) {
                    maxDepth = depth;
                    deepestSpan = span as HTMLElement;
                }
            });

            console.log(`Found deepest span at depth: ${maxDepth}`);
            return deepestSpan;
        } catch (e) {
            console.warn('Error finding deepest span', e);
            return null;
        }
    },

    /**
     * Find all occurrences of a search term in the editor
     */
    findAll(editor: EditorElement | null, searchTerm: string, caseSensitive: boolean = false, selectFirstResult: boolean = false): number {
        if (!editor || !searchTerm) {
            console.warn('Editor or search term is missing');
            return 0;
        }

        try {
            // Clear previous highlights
            this.clearFindHighlights(editor);
            this.findMatches = [];
            this.currentMatchIndex = -1;
            this.findTerm = searchTerm;

            const selection = window.getSelection();
            if (!selection) return 0;

            // Get all text nodes in the editor
            const getAllTextNodes = (): Text[] => {
                const treeWalker = document.createTreeWalker(
                    editor,
                    NodeFilter.SHOW_TEXT,
                    null
                );

                const textNodes: Text[] = [];
                let node: Node | null;
                while ((node = treeWalker.nextNode())) {
                    textNodes.push(node as Text);
                }
                return textNodes;
            };

            // Search for the term - process nodes in reverse to avoid offset issues
            const searchRegex = new RegExp(
                searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                caseSensitive ? 'g' : 'gi'
            );

            let matchCount = 0;

            // Work through text nodes backwards to avoid invalidating offsets
            const textNodes = getAllTextNodes();
            
            for (let i = textNodes.length - 1; i >= 0; i--) {
                const textNode = textNodes[i];
                const text = textNode.textContent || '';
                
                // Find all matches in this text node
                const matches: { index: number; length: number }[] = [];
                let match: RegExpExecArray | null;
                
                while ((match = searchRegex.exec(text)) !== null) {
                    matches.push({
                        index: match.index,
                        length: match[0].length
                    });
                }

                // Process matches in reverse order within this text node
                for (let j = matches.length - 1; j >= 0; j--) {
                    const matchInfo = matches[j];
                    
                    try {
                        const range = document.createRange();
                        range.setStart(textNode, matchInfo.index);
                        range.setEnd(textNode, matchInfo.index + matchInfo.length);

                        // Highlight the match
                        const mark = document.createElement('mark');
                        mark.className = 'rich-text-editor-find-highlight';
                        mark.style.backgroundColor = '#ffff00';
                        mark.style.color = '#000';
                        
                        range.surroundContents(mark);
                        matchCount++;
                    } catch (e) {
                        console.warn('Could not highlight match', e);
                    }
                }
            }

            // Rebuild matches in forward (document) order from the DOM after all highlights are inserted.
            // The reverse-order processing above was only to keep text-node offsets valid during insertion;
            // navigation (findNext/findPrevious) expects matches[0] to be the first match in reading order.
            const marks = editor.querySelectorAll('.rich-text-editor-find-highlight');
            this.findMatches = Array.from(marks).map(mark => {
                const range = document.createRange();
                range.selectNodeContents(mark);
                return range;
            });

            console.log(`Found ${matchCount} matches for "${searchTerm}"`);
            
            // Select first match if any
            if (this.findMatches.length > 0 && selectFirstResult) {
                this.selectMatch(0);
            }

            return matchCount;
        } catch (e) {
            console.error('Find operation failed', e);
            return 0;
        }
    },

    /**
     * Navigate to a specific match
     */
    selectMatch(index: number): boolean {
        if (index < 0 || index >= this.findMatches.length) {
            return false;
        }

        try {
            // Remove highlight from previous match
            const marks = document.querySelectorAll('.rich-text-editor-find-highlight');
            marks.forEach(mark => mark.classList.remove('rich-text-editor-find-current'));

            this.currentMatchIndex = index;
            const mark = marks[index] as HTMLElement;
            
            if (mark) {
                mark.classList.add('rich-text-editor-find-current');
                mark.style.backgroundColor = '#ff9900';
                
                // Scroll into view
                mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
                
                // Select the text
                const range = document.createRange();
                range.selectNodeContents(mark);
                const selection = window.getSelection();
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }

            console.log(`Selected match ${index + 1} of ${this.findMatches.length}`);
            return true;
        } catch (e) {
            console.warn('Failed to select match', e);
            return false;
        }
    },

    /**
     * Navigate to the next match
     */
    findNext(editor: EditorElement | null): boolean {
        if (this.findMatches.length === 0) {
            console.warn('No matches to navigate');
            return false;
        }

        const nextIndex = (this.currentMatchIndex + 1) % this.findMatches.length;
        return this.selectMatch(nextIndex);
    },

    /**
     * Navigate to the previous match
     */
    findPrevious(editor: EditorElement | null): boolean {
        if (this.findMatches.length === 0) {
            console.warn('No matches to navigate');
            return false;
        }

        const prevIndex = this.currentMatchIndex <= 0 
            ? this.findMatches.length - 1 
            : this.currentMatchIndex - 1;
        return this.selectMatch(prevIndex);
    },

    /**
     * Replace the current match
     */
    replaceCurrent(editor: EditorElement | null, replaceTerm: string): boolean {
        if (!editor || this.currentMatchIndex < 0 || this.currentMatchIndex >= this.findMatches.length) {
            console.warn('No current match to replace');
            return false;
        }

        try {
            const marks = document.querySelectorAll('.rich-text-editor-find-highlight');
            const currentMark = marks[this.currentMatchIndex] as HTMLElement;

            if (currentMark) {
                const textNode = document.createTextNode(replaceTerm);
                currentMark.parentNode?.replaceChild(textNode, currentMark);
                
                console.log(`Replaced match ${this.currentMatchIndex + 1}`);
                
                // Re-run find to update matches
                if (this.findTerm) {
                    this.findAll(editor, this.findTerm, false);
                }
                
                return true;
            }

            return false;
        } catch (e) {
            console.error('Replace operation failed', e);
            return false;
        }
    },

    /**
     * Replace all occurrences
     */
    replaceAll(editor: EditorElement | null, searchTerm: string, replaceTerm: string, caseSensitive: boolean = false): number {
        if (!editor || !searchTerm) {
            console.warn('Editor or search term is missing');
            return 0;
        }

        try {
            // First find all matches
            this.findAll(editor, searchTerm, caseSensitive);
            const matchCount = this.findMatches.length;

            if (matchCount === 0) {
                console.log('No matches found to replace');
                return 0;
            }

            // Replace all marks from last to first (to avoid index issues)
            const marks = document.querySelectorAll('.rich-text-editor-find-highlight');
            for (let i = marks.length - 1; i >= 0; i--) {
                const mark = marks[i] as HTMLElement;
                const textNode = document.createTextNode(replaceTerm);
                mark.parentNode?.replaceChild(textNode, mark);
            }

            // Clear find state
            this.findMatches = [];
            this.currentMatchIndex = -1;

            console.log(`Replaced ${matchCount} occurrences of "${searchTerm}" with "${replaceTerm}"`);
            return matchCount;
        } catch (e) {
            console.error('Replace all operation failed', e);
            return 0;
        }
    },

    /**
     * Clear all find/replace highlights
     */
    clearFindHighlights(editor: EditorElement | null): void {
        if (!editor) return;

        try {
            const marks = editor.querySelectorAll('.rich-text-editor-find-highlight');
            marks.forEach((mark) => {
                // Move children out of mark and remove it
                while (mark.firstChild) {
                    mark.parentNode?.insertBefore(mark.firstChild, mark);
                }
                mark.remove();
            });

            this.findMatches = [];
            this.currentMatchIndex = -1;
            this.findTerm = '';

            console.log('Find highlights cleared');
        } catch (e) {
            console.warn('Error clearing find highlights', e);
        }
    },

    /**
     * Get current find/replace state
     */
    getFindState(): { matchCount: number; currentIndex: number; searchTerm: string } {
        return {
            matchCount: this.findMatches.length,
            currentIndex: this.currentMatchIndex,
            searchTerm: this.findTerm
        };
    },

    /**
    * Toggle superscript on selected text.
    * Handles selections that span multiple <p> elements.
    */
    toggleSuperscript(editor: EditorElement | null): boolean {
        if (!editor) {
            console.warn('Editor is missing');
            return false;
        }

        try {
            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0) {
                console.warn('No selection available');
                return false;
            }

            const range = selection.getRangeAt(0);

            if (range.collapsed) {
                console.warn('No text selected for superscript');
                return false;
            }

            // Check if selection is already in a <sup> tag — if so, unwrap it.
            let currentNode: Node = range.commonAncestorContainer;
            if (currentNode.nodeType === Node.TEXT_NODE) {
                currentNode = currentNode.parentElement!;
            }

            let existingSup: HTMLElement | null = null;
            let checkNode = currentNode as HTMLElement;
            while (checkNode && checkNode !== editor) {
                if (checkNode.tagName.toLowerCase() === 'sup') {
                    existingSup = checkNode;
                    break;
                }
                checkNode = checkNode.parentElement!;
            }

            if (existingSup) {
                while (existingSup.firstChild) {
                    existingSup.parentNode?.insertBefore(existingSup.firstChild, existingSup);
                }
                existingSup.remove();
                console.log('Removed superscript');
                return true;
            }

            this._applyWrapperToRange(range, editor, () => document.createElement('sup'));

            console.log('Applied superscript');
            return true;
        } catch (e) {
            console.error('Superscript toggle failed', e);
            return false;
        }
    },

    /**
     * Toggle subscript on selected text.
     * Handles selections that span multiple <p> elements.
     */
    toggleSubscript(editor: EditorElement | null): boolean {
        if (!editor) {
            console.warn('Editor is missing');
            return false;
        }

        try {
            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0) {
                console.warn('No selection available');
                return false;
            }

            const range = selection.getRangeAt(0);

            if (range.collapsed) {
                console.warn('No text selected for subscript');
                return false;
            }

            // Check if selection is already in a <sub> tag — if so, unwrap it.
            let currentNode: Node = range.commonAncestorContainer;
            if (currentNode.nodeType === Node.TEXT_NODE) {
                currentNode = currentNode.parentElement!;
            }

            let existingSub: HTMLElement | null = null;
            let checkNode = currentNode as HTMLElement;
            while (checkNode && checkNode !== editor) {
                if (checkNode.tagName.toLowerCase() === 'sub') {
                    existingSub = checkNode;
                    break;
                }
                checkNode = checkNode.parentElement!;
            }

            if (existingSub) {
                while (existingSub.firstChild) {
                    existingSub.parentNode?.insertBefore(existingSub.firstChild, existingSub);
                }
                existingSub.remove();
                console.log('Removed subscript');
                return true;
            }

            this._applyWrapperToRange(range, editor, () => document.createElement('sub'));

            console.log('Applied subscript');
            return true;
        } catch (e) {
            console.error('Subscript toggle failed', e);
            return false;
        }
    },

    /**
     * Clear all formatting and flatten nested spans into a single span
     */
    clearAllFormatting(editor: EditorElement | null): void {
        if (!editor) { 
            console.warn('Editor is missing');
            return;
        }

        try {
            // Get all text content, stripping all formatting
            const textContent = this.getTextContent(editor);
            
            // Clear the editor and set plain text
            editor.innerHTML = textContent;
            
            // Move cursor to end
            const range = document.createRange();
            const sel = window.getSelection();
            
            if (editor.childNodes.length > 0) {
                const lastNode = editor.childNodes[editor.childNodes.length - 1];
                range.setStartAfter(lastNode);
                range.collapse(true);
                sel?.removeAllRanges();
                sel?.addRange(range);
            }
            
            this.focus(editor);
        } catch (e) {
            console.warn('Clear formatting error:', e);
        }
    },

    /**
     * Schedule a .NET callback after the user stops typing.
     * Uses the browser's native setTimeout so no .NET async machinery is involved during the debounce wait — 
     * calling this on every keystroke simply resets the timer without touching the Blazor task scheduler.
     */
    scheduleInputCommit(dotNetRef: any, delayMs: number): void {
        if (this.commitTimer !== null) {
            clearTimeout(this.commitTimer);
        }

        this.commitTimer = setTimeout(() => {
            this.commitTimer = null;
            dotNetRef.invokeMethodAsync('OnInputCommitDebounced');
        }, delayMs);
    },

    /**
     * Cancel any pending input-commit timer (e.g. when undo/redo fires mid-session).
     */
    cancelInputCommit(): void {
        if (this.commitTimer === null) {
            console.warn('Commit Timer is empty');
            return;
        }

        clearTimeout(this.commitTimer);
        this.commitTimer = null;
    },

    /**
     * Apply a line height value to the editor element.
     * The value is a unitless multiplier (e.g. 1.0, 1.5, 2.0).
     */
    applyLineHeight(editor: EditorElement | null, lineHeight: number): void {
        if (!editor || lineHeight <= 0) { 
            console.warn('Editor is missing');
            return;
        }

        try {
            editor.style.lineHeight = String(lineHeight);
        } catch (e) {
            console.warn('Failed to apply line height', e);
        }
    },

    /**
     * Apply a tracking (letter-spacing) value to the editor element.
     * The value is in em units (e.g. 0 = normal, 0.1 = loose, -0.05 = tight).
     */
    applyTracking(editor: EditorElement | null, tracking: number): void {
        if (!editor) {
            console.warn('Editor is missing');
            return;
        }

        try {
            editor.style.letterSpacing = tracking === 0 ? '' : `${tracking}em`;
        } catch (e) {
            console.warn('Failed to apply tracking', e);
        }
    },

    /**
     * Returns true when the cursor is collapsed and sits between two typeable characters, accounting for all cursor positions:
     * - Mid text node: both neighbours are within the same text node
     * - Boundary of a text node: one neighbour is in an adjacent sibling (e.g. a kerning span)
     * - Element node: cursor is at a child index between two child nodes
     */
    isCursorBetweenChars(editor: EditorElement | null): boolean {
        if (!editor) {
            console.warn('Editor is missing');
            return false;
        }

        try {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) {
                return false;
            }

            const range = selection.getRangeAt(0);
            if (!range.collapsed) {
                return false;
            }

            const isTypeable = (c: string | null): boolean =>
                c !== null && c !== '\u200B' && /\S/.test(c);

            const getFirstChar = (node: Node): string | null => {
                const text = node.textContent || '';
                for (let i = 0; i < text.length; i++) {
                    if (isTypeable(text[i])) return text[i];
                }
                return null;
            };

            const getLastChar = (node: Node): string | null => {
                const text = node.textContent || '';
                for (let i = text.length - 1; i >= 0; i--) {
                    if (isTypeable(text[i])) return text[i];
                }
                return null;
            };

            let charBefore: string | null = null;
            let charAfter: string | null = null;

            const container = range.startContainer;
            const offset = range.startOffset;

            if (container.nodeType === Node.TEXT_NODE) {
                const text = container.textContent || '';

                if (offset > 0) {
                    // Char exists within the same text node before the cursor
                    charBefore = text[offset - 1] !== '\u200B' ? text[offset - 1] : null;
                } else {
                    // Cursor is at the very start of this text node — check the previous sibling
                    const prev = container.previousSibling;
                    if (prev) {
                        charBefore = getLastChar(prev);
                    }
                }

                if (offset < text.length) {
                    // Char exists within the same text node after the cursor
                    charAfter = text[offset] !== '\u200B' ? text[offset] : null;
                } else {
                    // Cursor is at the very end of this text node — check the next sibling
                    const next = container.nextSibling;
                    if (next) {
                        charAfter = getFirstChar(next);
                    }
                }
            } else {
                // Cursor is an element-level position (startContainer is an element, startOffset is a child index). 
                // This occurs when sitting between two child nodes such as a text node and a kerning span.
                const childBefore = offset > 0 ? container.childNodes[offset - 1] : null;
                const childAfter = offset < container.childNodes.length ? container.childNodes[offset] : null;

                if (childBefore) {
                    charBefore = getLastChar(childBefore);
                }
                if (childAfter) {
                    charAfter = getFirstChar(childAfter);
                }
            }

            return isTypeable(charBefore) && isTypeable(charAfter);
        } catch (e) {
            console.warn('Error in isCursorBetweenChars', e);
            return false;
        }
    },

    /**
     * Returns the current kerning state:
     * - isBetweenChars: whether the cursor is between two typeable characters
     * - pairKerningThousandths: the letter-spacing on the kerning span immediately after the cursor 
     *      (in thousandths of an em, matching the tracking input scale), or 0 if none is present
     * - globalKerning: the CSS font-kerning value on the editor element ('auto', 'normal', or 'none')
     */
    getKerningState(editor: EditorElement | null): { isBetweenChars: boolean; pairKerningThousandths: number; globalKerning: string } {
        const defaultState = { isBetweenChars: false, pairKerningThousandths: 0, globalKerning: 'auto' };

        if (!editor) {
            console.warn('Editor is missing');
            return defaultState;
        }

        try {
            const isBetweenChars = this.isCursorBetweenChars(editor);
            const rawKerning = editor.style.fontKerning;
            const globalKerning = rawKerning === '' ? 'auto' : rawKerning;

            let pairKerningThousandths = 0;

            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                // Persist the selection so toolbar interactions can restore it
                this.savedSelection = {
                    startContainer: range.startContainer,
                    startOffset: range.startOffset,
                    endContainer: range.endContainer,
                    endOffset: range.endOffset,
                };

                if (isBetweenChars) {
                    const container = range.startContainer;
                    const nextSibling = container.nextSibling as HTMLElement | null;
                    if (
                        nextSibling &&
                        nextSibling.nodeType === Node.ELEMENT_NODE &&
                        nextSibling.tagName.toLowerCase() === 'span' &&
                        nextSibling.dataset?.kerning === 'true'
                    ) {
                        const ls = nextSibling.style.letterSpacing;
                        if (ls && ls.endsWith('em')) {
                            pairKerningThousandths = parseFloat(ls) * 1000;
                        }
                    }
                }
            }

            return { isBetweenChars, pairKerningThousandths, globalKerning };
        } catch (e) {
            console.warn('Error in getKerningState', e);
            return defaultState;
        }
    },

        /**
     * Wraps the character immediately before the cursor in a kerning span with the given letter-spacing value
     * (expressed in thousandths of an em, the same scale used by the tracking control).
     * Passing 0 removes any existing kerning span and restores the raw character.
     *
     * Handles three cursor positions:
     *  - Mid text node    : cursor sits between two chars within the same Text node
     *  - Start of text node : cursor is at offset 0, previous sibling is an existing kern span
     *  - Element level    : startContainer is an element; childNodes[offset] is the Text
     *                       node and childNodes[offset-1] is the existing kern span
     */
    applyPairKerning(editor: EditorElement | null, thousandths: number): void {
        if (!editor) {
            console.warn('Editor is missing');
            return;
        }

        try {
            // Restore the selection saved when the user last clicked inside the editor.
            // Any toolbar interaction steals focus, so we must reinstate it manually before performing any cursor-relative DOM work.
            if (this.savedSelection) {
                editor.focus();
                try {
                    const restoredRange = document.createRange();
                    restoredRange.setStart(this.savedSelection.startContainer, this.savedSelection.startOffset);
                    restoredRange.setEnd(this.savedSelection.endContainer, this.savedSelection.endOffset);
                    const sel = window.getSelection();
                    if (sel) {
                        sel.removeAllRanges();
                        sel.addRange(restoredRange);
                    }
                } catch (restoreErr) {
                    console.warn('Failed to restore saved selection', restoreErr);
                }
            }

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) { 
                return;
            }

            const range = selection.getRangeAt(0);
            if (!range.collapsed) { 
                return;
            }

            const container = range.startContainer;
            const offset = range.startOffset;

            // ── Resolve the "after" text node and the text that precedes the cursor ──
            //
            // beforeText   = the char to kern (immediately before the cursor) + any
            //                remaining text that currently sits before it (inside the
            //                kern span and/or the preceding text node)
            // afterTextNode = the Text node that will remain unchanged to the RIGHT of
            //                 the cursor; its .textContent must already reflect only the
            //                 "after" content by the time we reach the insertion step.

            let afterTextNode: Text;
            let beforeText: string;
            let existingKernSpan: HTMLElement | null = null;

            if (container.nodeType === Node.TEXT_NODE) {
                const text = container.textContent || '';

                if (offset > 0) {
                    // ── Case A: mid-text-node (first-time apply or repositioned cursor) ──
                    afterTextNode = container as Text;
                    // Split the text node in place; beforeText ends with the char to kern.
                    beforeText = text.substring(0, offset);
                    afterTextNode.textContent = text.substring(offset);

                } else {
                    // ── Case B: cursor at start of text node ──
                    // Only valid if the immediately preceding sibling is our kerning span.
                    const prev = container.previousSibling as HTMLElement | null;
                    if (
                        prev &&
                        prev.nodeType === Node.ELEMENT_NODE &&
                        prev.tagName.toLowerCase() === 'span' &&
                        prev.dataset?.kerning === 'true'
                    ) {
                        afterTextNode = container as Text;
                        existingKernSpan = prev;
                        beforeText = '';
                    } else {
                        return;
                    }
                }
            } else {
                // ── Case C: element-level cursor (child index) ──
                // Occurs naturally after a kern span is inserted: the browser places the
                // cursor as an element-level position between the span and the text node.
                const childBefore = offset > 0 ? container.childNodes[offset - 1] : null;
                const childAfter  = offset < container.childNodes.length ? container.childNodes[offset] : null;

                if (!childAfter || childAfter.nodeType !== Node.TEXT_NODE) { 
                    return;
                }

                if (
                    !childBefore ||
                    (childBefore as HTMLElement).tagName?.toLowerCase() !== 'span' ||
                    (childBefore as HTMLElement).dataset?.kerning !== 'true'
                ) { 
                    return;
                }

                afterTextNode = childAfter as Text;
                existingKernSpan = childBefore as HTMLElement;
                beforeText = '';
            }

            const parent = afterTextNode.parentNode;
            if (!parent) { 
                return;
            }

            // ── Collect text from the existing kern span and remove it ──
            // We do NOT call normalize() here because it merges text nodes and
            // invalidates any live Range references, causing the cursor to jump.
            // Instead we gather all affected text manually and rebuild it ourselves.
            if (existingKernSpan) {
                beforeText = (existingKernSpan.textContent || '') + beforeText;

                // The text node that precedes the kern span holds the "remaining before" text.
                const nodeBeforeKernSpan = existingKernSpan.previousSibling;
                if (nodeBeforeKernSpan && nodeBeforeKernSpan.nodeType === Node.TEXT_NODE) {
                    beforeText = (nodeBeforeKernSpan.textContent || '') + beforeText;
                    parent.removeChild(nodeBeforeKernSpan);
                }

                parent.removeChild(existingKernSpan);
            }

            if (!beforeText) { 
                return;
            }

            if (thousandths === 0) {
                // ── Remove kerning: merge beforeText back into afterTextNode ──
                afterTextNode.textContent = beforeText + (afterTextNode.textContent || '');

                this.savedSelection = {
                    startContainer: afterTextNode,
                    startOffset: beforeText.length,
                    endContainer: afterTextNode,
                    endOffset: beforeText.length,
                };

                // Defer cursor restoration past Blazor's synchronous render batch.
                // The render batch runs within the current JS task; rAF fires after it.
                const restoreTarget = this.savedSelection;
                requestAnimationFrame(() => {
                    try {
                        const r = document.createRange();
                        r.setStart(restoreTarget.startContainer, restoreTarget.startOffset);
                        r.setEnd(restoreTarget.endContainer, restoreTarget.endOffset);
                        const sel = window.getSelection();
                        if (sel) { sel.removeAllRanges(); sel.addRange(r); }
                        if (document.activeElement !== editor) editor.focus({ preventScroll: true });
                    } catch (e) { 
                        console.warn('rAF cursor restore failed (zero kerning)', e); 
                    }
                });

                console.log('Pair kerning removed');
                return;
            }

            // ── Apply kerning: wrap beforeText's last char in a kerning span ──
            const kernedChar     = beforeText[beforeText.length - 1];
            const remainingBefore = beforeText.substring(0, beforeText.length - 1);

            const kerningSpan = document.createElement('span');
            kerningSpan.dataset.kerning = 'true';
            kerningSpan.style.letterSpacing = `${thousandths / 1000}em`;
            kerningSpan.textContent = kernedChar;

            const beforeTextNode = document.createTextNode(remainingBefore);

            parent.insertBefore(beforeTextNode, afterTextNode);
            parent.insertBefore(kerningSpan, afterTextNode);

            this.savedSelection = {
                startContainer: afterTextNode,
                startOffset: 0,
                endContainer: afterTextNode,
                endOffset: 0,
            };

            // Defer cursor restoration past Blazor's synchronous render batch.
            // The render batch runs within the current JS task; rAF fires after it.
            const restoreTarget = this.savedSelection;
            requestAnimationFrame(() => {
                try {
                    const r = document.createRange();
                    r.setStart(restoreTarget.startContainer, restoreTarget.startOffset);
                    r.setEnd(restoreTarget.endContainer, restoreTarget.endOffset);
                    const sel = window.getSelection();

                    if (sel) { 
                        sel.removeAllRanges(); sel.addRange(r); 
                    }

                    if (document.activeElement !== editor) { 
                        editor.focus({ preventScroll: true });
                    }
                } catch (e) { 
                    console.warn('rAF cursor restore failed', e); 
                }
            });

            console.log(`Applied pair kerning: ${thousandths} thousandths of an em`);
        } catch (e) {
            console.warn('Failed to apply pair kerning', e);
        }
    },

    /**
     * Apply the CSS font-kerning property to the editor element.
     * Accepts 'auto', 'normal', or 'none'.
     */
    applyGlobalKerning(editor: EditorElement | null, value: string): void {
        if (!editor) { 
            console.warn('Editor is missing');
            return;
        }

        try {
            editor.style.fontKerning = value === 'auto' ? '' : value;
            console.log(`Applied global kerning: ${value}`);
        } catch (e) {
            console.warn('Failed to apply global kerning', e);
        }
    },

    /**
     * Reinstates the selection that was saved to this.savedSelection e.g. via getKerningState or applyPairKerning
    */
    restoreSavedSelection(editor: EditorElement | null): void {
        if (!editor || !this.savedSelection) {
            return;
        }

        try {
            editor.focus();
            const range = document.createRange();
            range.setStart(this.savedSelection.startContainer, this.savedSelection.startOffset);
            range.setEnd(this.savedSelection.endContainer, this.savedSelection.endOffset);
            const sel = window.getSelection();

            if (sel) {
                sel.removeAllRanges();
                sel.addRange(range);
            }
            console.log('Restored saved selection after render');
        } catch (e) {
            console.warn('Failed to restore saved selection', e);
        }
    },

    /**
     * Apply font colour to the selected text or insert a coloured span at the cursor if no selection exists.
     * Handles selections that span multiple <p> elements.
     */
    applyFontColour(editor: EditorElement | null, cssColour: string): void {
        if (!editor || !cssColour) {
            console.warn('Editor or cssColour is missing');
            return;
        }

        try {
            const selection = window.getSelection();
            if (!selection) {
                console.warn('No selection available');
                return;
            }

            const tryRestoreSavedSelection = (): Range | null => {
                if (!this.savedSelection) {
                    return null;
                }

                try {
                    const restored = document.createRange();
                    restored.setStart(this.savedSelection.startContainer, this.savedSelection.startOffset);
                    restored.setEnd(this.savedSelection.endContainer, this.savedSelection.endOffset);

                    if (!editor.contains(restored.commonAncestorContainer)) {
                        return null;
                    }

                    selection.removeAllRanges();
                    selection.addRange(restored);
                    return restored;
                } catch (e) {
                    console.warn('Failed to restore saved selection for font colour', e);
                    return null;
                }
            };

            let range: Range | null = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

            if (!range || range.collapsed) {
                range = tryRestoreSavedSelection();
            }

            if (!range) {
                console.warn('No valid range available for font colour');
                return;
            }

            if (!editor.contains(range.commonAncestorContainer)) {
                console.warn('Selection is outside editor');
                return;
            }

            if (range.collapsed) {
                // No selection — insert a styled span at cursor.
                const span = document.createElement('span');
                span.style.color = cssColour;
                const textNode = document.createTextNode('\u200B');
                span.appendChild(textNode);
                range.insertNode(span);

                const newRange = document.createRange();
                newRange.setStart(textNode, 1);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);

                this.savedSelection = {
                    startContainer: textNode,
                    startOffset: 1,
                    endContainer: textNode,
                    endOffset: 1,
                };

                console.log(`Inserted font colour span at cursor: ${cssColour}`);
                return;
            }

            // Has selection — wrap each paragraph-level portion in a coloured tspan.
            // _applyWrapperToRange handles both single-paragraph and cross-paragraph cases.
            const wrappers = this._applyWrapperToRange(range, editor, () => {
                const span = document.createElement('span');
                span.style.color = cssColour;
                return span;
            });

            if (wrappers.length === 0) {
                console.warn('applyFontColour: no wrappers inserted');
                return;
            }

            // Remove any explicit inline color from descendant spans inside each wrapper.
            // Without this, a pre-existing color on a child span overrides the parent via
            // specificity and the new colour is never visible in the editor.
            wrappers.forEach((wrapper) => {
                wrapper.querySelectorAll<HTMLElement>('span').forEach((inner) => {
                    inner.style.removeProperty('color');
                });
            });

            // Move the caret to the end of the last inserted wrapper and persist.
            const lastWrapper = wrappers[wrappers.length - 1];
            const newRange = document.createRange();
            newRange.selectNodeContents(lastWrapper);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);

            this.savedSelection = {
                startContainer: newRange.startContainer,
                startOffset: newRange.startOffset,
                endContainer: newRange.endContainer,
                endOffset: newRange.endOffset,
            };

            console.log(`Applied font colour to selection: ${cssColour}`);
        } catch (e) {
            console.warn('Failed to apply font colour', e);
        }
    },

    /**
     * Apply a stroke colour to selected text, or insert a stroked span at the
     * cursor when there is no active selection.
     */
    applyFontStrokeColour(editor: EditorElement | null, cssColour: string): void {
        if (!editor || !cssColour) {
            console.warn('Editor or cssColour is missing');
            return;
        }

        try {
            const selection = window.getSelection();

            if (!selection) {
                console.warn('No selection available');
                return;
            }

            let range: Range | null =
                selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

            if ((!range || range.collapsed) && this.savedSelection) {
                try {
                    const restoredRange = document.createRange();
                    restoredRange.setStart(
                        this.savedSelection.startContainer,
                        this.savedSelection.startOffset);
                    restoredRange.setEnd(
                        this.savedSelection.endContainer,
                        this.savedSelection.endOffset);

                    if (editor.contains(restoredRange.commonAncestorContainer)) {
                        selection.removeAllRanges();
                        selection.addRange(restoredRange);
                        range = restoredRange;
                    }
                } catch (error) {
                    console.warn('Failed to restore selection for font stroke colour', error);
                }
            }

            if (!range || !editor.contains(range.commonAncestorContainer)) {
                console.warn('No valid editor range available for font stroke colour');
                return;
            }

            const applyStroke = (element: HTMLElement): void => {
                // Use text-shadow for reliable rendering inside the HTML
                // contenteditable. Chromium can stop painting text-stroke on
                // a text run immediately before a space.
                element.style.setProperty(
                    'text-shadow',
                    [
                        `-1px -1px 0 ${cssColour}`,
                        `0 -1px 0 ${cssColour}`,
                        `1px -1px 0 ${cssColour}`,
                        `-1px 0 0 ${cssColour}`,
                        `1px 0 0 ${cssColour}`,
                        `-1px 1px 0 ${cssColour}`,
                        `0 1px 0 ${cssColour}`,
                        `1px 1px 0 ${cssColour}`,
                    ].join(', '));
            };

            if (range.collapsed) {
                const span = document.createElement('span');
                applyStroke(span);

                const textNode = document.createTextNode('\u200B');
                span.appendChild(textNode);
                range.insertNode(span);

                const newRange = document.createRange();
                newRange.setStart(textNode, 1);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);

                this.savedSelection = {
                    startContainer: textNode,
                    startOffset: 1,
                    endContainer: textNode,
                    endOffset: 1,
                };

                return;
            }

            const wrappers = this._applyWrapperToRange(range, editor, () => {
                const span = document.createElement('span');
                applyStroke(span);
                return span;
            });

            wrappers.forEach((wrapper) => {
                wrapper.querySelectorAll<HTMLElement>('span').forEach((inner) => {
                    inner.style.removeProperty('text-shadow');
                });
            });

            const lastWrapper = wrappers.at(-1);

            if (!lastWrapper) {
                return;
            }

            const newRange = document.createRange();
            newRange.selectNodeContents(lastWrapper);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);

            this.savedSelection = {
                startContainer: newRange.startContainer,
                startOffset: newRange.startOffset,
                endContainer: newRange.endContainer,
                endOffset: newRange.endOffset,
            };
        } catch (error) {
            console.warn('Failed to apply font stroke colour', error);
        }
    },

    /**
     * Fired on click of the editor to check the font-family at the cursor and invoke a .NET callback with the result
     */
    invokeFontFamilyChange(editor: EditorElement | null, dotNetRef: any) : void {
        if (!editor) {
            console.warn('Editor is missing');
            return;
        }

        const fontFamily = RichTextEditor.getFontFamilyAtCursor(editor);

        if (fontFamily) {
            dotNetRef.invokeMethodAsync('OnCursorFontFamilyChanged', fontFamily);
        }
    },

    /**
     * Returns the font-family of the span surrounding the current cursor position,  or null if no font-family can be resolved from the cursor's ancestor spans.
     *
     * Walk order:
     *  1. Start from the cursor's container node (or its parent if it's a text node).
     *  2. Walk upward through ancestors until the editor boundary is reached.
     *  3. Return the first font-family value found on a span's inline style.
     *
     * The returned string is the raw CSS value exactly as it appears in the style attribute (e.g. '"Avenir Next"' or 'Arial') — 
     * the caller is responsible for stripping surrounding quotes before displaying it in the UI.
     */
    getFontFamilyAtCursor(editor: EditorElement | null): string | null {
        if (!editor) {
            console.warn('getFontFamilyAtCursor: editor is missing');
            return null;
        }

        try {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) {
                return null;
            }

            const range = selection.getRangeAt(0);

            // Start from the container node; if it's a text node use its parent element.
            let node: Node | null = range.startContainer;
            if (node.nodeType === Node.TEXT_NODE) {
                node = node.parentElement;
            }

            // Walk up the tree looking for a span with an inline font-family.
            while (node && node !== editor) {
                if (
                    node.nodeType === Node.ELEMENT_NODE &&
                    (node as HTMLElement).tagName.toLowerCase() === 'span'
                ) {
                    const fontFamily = (node as HTMLElement).style.fontFamily;
                    if (fontFamily) {
                        // Strip wrapping quotes (e.g. '"Avenir Next"' → 'Avenir Next')
                        const cleaned = fontFamily.replace(/^["']|["']$/g, '').trim();
                        if (cleaned) {
                            console.log(`getFontFamilyAtCursor: resolved font-family "${cleaned}"`);
                            return cleaned;
                        }
                    }
                }
                node = (node as HTMLElement).parentElement;
            }

            console.log('getFontFamilyAtCursor: no font-family found in ancestor spans');
            return null;
        } catch (e) {
            console.warn('getFontFamilyAtCursor: error resolving font family', e);
            return null;
        }
    },

    /**
     * Clears the pretext layout cache that is used to avoid multiple expensive calls to the prepare method in the pretext library
     */
    clearPretextLayoutCache(): void {
        this.pretextPreparedCache.clear();
    },

    /**
     * Leverages the pretext library to compute the layout of the text in the editor, including line breaks for auto line breaks
     */
    getTextLayoutDetails(editor: EditorElement | null, maxWidth?: number, lineHeight?: number): RichTextLayoutResult | null {
        if (!editor) {
            console.warn('Editor is missing');
            return null;
        }

        try {
            const computed = window.getComputedStyle(editor);

            const fontSizePx = this._parsePixelValue(computed.fontSize, 16);
            const letterSpacingPx = this._getLetterSpacingPx(computed.letterSpacing, fontSizePx);
            const effectiveLineHeight = lineHeight && lineHeight > 0 ? lineHeight : this._getLineHeightPx(computed.lineHeight, fontSizePx);

            const width = Math.max(1, maxWidth ?? editor.clientWidth ?? 1);
            const textAlign = computed.textAlign || 'left';
            const text = this._extractPlainTextForLayout(editor);
            const font = this._buildCanvasFontShorthand(computed);

            //whiteSpace: pre-wrap ensure text-area like text where spaces, tabs and hard breaks stay visible
            const prepareOptions: PrepareOptions = {
                whiteSpace: 'pre-wrap',
                wordBreak: 'normal',
                letterSpacing: letterSpacingPx,
            };

            const cacheKey = `${text}|${font}|${prepareOptions.whiteSpace}|${prepareOptions.wordBreak}|${prepareOptions.letterSpacing}`;
            let prepared = this.pretextPreparedCache.get(cacheKey);

            if (!prepared) {
                prepared = prepareWithSegments(text, font, prepareOptions);
                this.pretextPreparedCache.set(cacheKey, prepared);

                if (this.pretextPreparedCache.size > PRETEXT_CACHE_MAX_SIZE) {
                    const firstKey = this.pretextPreparedCache.keys().next().value;
                    if (firstKey) { 
                        this.pretextPreparedCache.delete(firstKey);
                    }
                }
            }

            const metrics = layout(prepared, width, effectiveLineHeight);
            const withLines = layoutWithLines(prepared, width, effectiveLineHeight);

            const toSvgAnchor = (align: string): 'start' | 'middle' | 'end' => {
                const v = align.toLowerCase();

                if (v === 'center') { 
                    return 'middle';
                
                }

                if (v === 'right' || v === 'end') { 
                    return 'end'; 
                }

                return 'start';
            };

            const resolveX = (lineWidth: number): number => {
                const v = textAlign.toLowerCase();

                if (v === 'center') { 
                    return width / 2; 
                }

                if (v === 'right' || v === 'end') { 
                    return width; 
                }

                return 0;
            };

            const lines: RichTextLayoutLine[] = withLines.lines.map((line, i) => {
                const baselineY = (i + 1) * effectiveLineHeight;
                return {
                    index: i,
                    text: line.text,
                    width: line.width,
                    x: resolveX(line.width),
                    y: baselineY,
                    baselineY,
                };
            });

            const maxLineWidth = withLines.lines.reduce((m, l) => Math.max(m, l.width), 0);

            const result: RichTextLayoutResult = {
                text,
                font,
                maxWidth: width,
                maxLineWidth,
                lineHeight: effectiveLineHeight,
                lineCount: metrics.lineCount,
                height: metrics.height,
                textAlign,
                svg: {
                    textAnchor: toSvgAnchor(textAlign),
                    width,
                    height: metrics.height,
                    lines: lines
                }
            };

            return result;
        } catch (e) {
            console.warn('Failed to compute Pretext layout', e);
            return null;
        }
    },

    /**
     * Applies a text direction ('ltr' or 'rtl') to the editor.
     *
     * Behaviour depends on whether the user has an active text selection inside the editor:
     *
     * — Selection present: wraps each selected paragraph-level segment in a <span> carrying
     *   an explicit `direction` inline style, leaving the rest of the content untouched.
     *   Returns false so the caller knows the global direction state was NOT changed.
     *
     * — No selection: applies the direction to every <p> and nested <span> in the editor,
     *   mirroring Adobe Illustrator's paragraph-level Direction control.
     *   Returns true so the caller knows the global direction state WAS changed.
     */
    applyTextDirection(editor: EditorElement | null, direction: 'ltr' | 'rtl'): boolean {
        if (!editor) {
            console.warn('Editor is missing');
            return false;
        }

        try {
            const selection = window.getSelection();
            const hasSelection =
                selection !== null &&
                selection.rangeCount > 0 &&
                !selection.getRangeAt(0).collapsed &&
                editor.contains(selection.getRangeAt(0).commonAncestorContainer);
                 
            if (hasSelection) {
                //Selection present: apply direction only to the selected text
                const range = selection!.getRangeAt(0);
                const selectedText = range.toString();

                // Check whether the selection is already fully covered by a single <bdo>
                // whose text content matches the selection exactly. If so, update that
                // element's direction in-place rather than nesting a new <bdo> inside it.
                const existingBdo = this._findEnclosingElement(range, editor, 'bdo');
                if (existingBdo && existingBdo.textContent === selectedText) {
                    existingBdo.style.direction = direction;
                    existingBdo.style.unicodeBidi = 'isolate';
                    console.log(
                        `Updated existing <bdo> direction to "${direction}" (text matched: "${selectedText.substring(0, 40)}")`
                    );
                    return false;
                }

                const wrappers = this._applyWrapperToRange(range, editor, () => {
                    const span = document.createElement('bdo');
                    span.style.direction = direction;
                    // unicode-bidi must be set alongside direction so the bidi algorithm
                    // actually resets at this element boundary rather than inheriting.
                    span.style.unicodeBidi = 'isolate';
                    return span;
                });

                console.log(
                    `Applied text direction: ${direction} to ${wrappers.length} selected segment(s) (selection-only; global direction unchanged)`
                );

                // Return false — the caller should NOT update its global direction state.
                return false;
            }

            //No selection: apply direction to all paragraphs (global)
            const paragraphs = editor.querySelectorAll('p');
            paragraphs.forEach((p) => {
                p.style.direction = direction;
            });

            console.log(
                `Applied text direction: ${direction} (global — editor + ${editor.querySelectorAll('p span').length} span(s))`
            );

            // Return true — the caller should update its global direction state.
            return true;
        } catch (e) {
            console.warn('Failed to apply text direction', e);
            return false;
        }
    },

    /**
     * Returns the explicit text direction of the current selection by walking up the DOM from the selection's common ancestor container, 
     * looking for the first ancestor element (span or p) that carries an inline direction style.
     *
     * Return values:
     *   'rtl'  — the nearest ancestor with an explicit direction is RTL
     *   'ltr'  — the nearest ancestor with an explicit direction is LTR
     *   null   — the selection is collapsed, outside the editor, or no ancestor
     *            carries an explicit direction style (treat as unset / LTR)
     */
    getSelectionTextDirection(editor: EditorElement | null): string | null {
        if (!editor) {
            return null;
        }

        try {
            const selection = window.getSelection();

            if (!selection || selection.rangeCount === 0) {
                return null;
            }

            const range = selection.getRangeAt(0);

            // Collapsed range means no text is selected — nothing to inspect.
            if (range.collapsed) {
                return null;
            }

            if (!editor.contains(range.commonAncestorContainer)) {
                return null;
            }

            // Start the ancestor walk from the common ancestor container.
            // If it is a text node, step up to its parent element first.
            let node: Node | null = range.commonAncestorContainer;
            if (node.nodeType === Node.TEXT_NODE) {
                node = node.parentElement;
            }

            while (node && node !== editor) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const el = node as HTMLElement;
                    // Inline style takes precedence (it's what applyTextDirection writes), then the dir attribute.
                    const dir = (el.style.direction || el.getAttribute('dir') || '').toLowerCase();
                    if (dir === 'rtl' || dir === 'ltr') {
                        console.log(`getSelectionTextDirection: found direction="${dir}" on <${el.tagName.toLowerCase()}>`);
                        return dir;
                    }
                }
                node = (node as HTMLElement).parentElement;
            }

            // No ancestor inside the editor carries an explicit direction.
            console.log('getSelectionTextDirection: no explicit direction found on selection ancestors');
            return null;
        } catch (e) {
            console.warn('getSelectionTextDirection: error', e);
            return null;
        }
    },

    /**
     * Searches the editor for all elements matching the supplied tag name (case-insensitive) and returns the innermost one whose DOM subtree fully
     * contains both the effective start and end nodes of the given range, or null if none does.
     *
     * Elements are iterated in reverse document order (innermost first) so that when elements of the same tag are nested the deepest matching element wins.
     */
    _findEnclosingElement(range: Range, editor: EditorElement, tagName: string): HTMLElement | null {
        const normalizedTag = tagName.toLowerCase();

        // Resolve an element-level boundary point down to the concrete child node it refers to. 
        // When the browser places a range boundary inside an element (rather than inside a text node) it sets:
        //   container = the parent element
        //   offset    = the index of the child at that boundary
        // Node.contains() requires a node that IS inside the candidate, so we must dereference the offset to the actual child. 
        // For the start boundary the child at [offset] is the first selected node; for the end boundary the child at [offset - 1] is the last selected node.
        const resolveNode = (container: Node, offset: number, isEnd: boolean): Node => {
            if (container.nodeType !== Node.ELEMENT_NODE) {
                return container;
            }

            const children = container.childNodes;
            const index = isEnd ? offset - 1 : offset;

            if (index >= 0 && index < children.length) {
                return children[index];
            }

            // Offset is out of range — fall back to the container itself.
            return container;
        };

        const startNode = resolveNode(range.startContainer, range.startOffset, false);
        const endNode   = resolveNode(range.endContainer,   range.endOffset,   true);

        // querySelectorAll returns elements in document order (outermost first).
        // Reversing gives innermost first so the deepest match wins when elements of the same tag are nested.
        const candidates = Array.from(editor.querySelectorAll<HTMLElement>(normalizedTag)).reverse();

        for (const candidate of candidates) {
            try {
                if (candidate.contains(startNode) && candidate.contains(endNode)) {
                    return candidate;
                }
            } catch (e) {
                console.warn(`_findEnclosingElement: contains check failed for <${normalizedTag}>`, e);
            }
        }

        return null;
    },

    _parsePixelValue(value: string, fallback: number): number {
        if (!value || value === 'normal') {
            return fallback;
        }

        const n = parseFloat(value);
        return Number.isFinite(n) ? n : fallback;
    },

    _getLineHeightPx(lineHeight: string, fontSizePx: number): number {
        if (!lineHeight || lineHeight === 'normal') {
            return fontSizePx * 1.2;
        }

        if (lineHeight.endsWith('px')) { 
            return this._parsePixelValue(lineHeight, fontSizePx * 1.2);
        }

        const unitless = parseFloat(lineHeight);
        return Number.isFinite(unitless) ? unitless * fontSizePx : fontSizePx * 1.2;
    },

    _getLetterSpacingPx(letterSpacing: string, fontSizePx: number): number {
        if (!letterSpacing || letterSpacing === 'normal') { 
            return 0;
        }

        if (letterSpacing.endsWith('px')) { 
            return this._parsePixelValue(letterSpacing, 0);
        }

        if (letterSpacing.endsWith('em')) { 
            return this._parsePixelValue(letterSpacing, 0) * fontSizePx;
        }

        return this._parsePixelValue(letterSpacing, 0);
    },

    _buildCanvasFontShorthand(style: CSSStyleDeclaration): string {
        const fontStyle = style.fontStyle || 'normal';
        const fontWeight = style.fontWeight || '400';
        const fontSize = style.fontSize || '16px';
        const fontFamily = style.fontFamily || 'Avenir';

        return `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
    },

    _extractPlainTextForLayout(root: HTMLElement): string {
        const blockTags = new Set(['DIV', 'P', 'LI', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER']);
        let output = '';

        const walk = (node: Node): void => {
            if (node.nodeType === Node.TEXT_NODE) {
                output += node.textContent ?? '';
                return;
            }

            if (node.nodeType !== Node.ELEMENT_NODE) { 
                return;
            }

            const el = node as HTMLElement;
            const tag = el.tagName.toUpperCase();

            if (tag === 'BR') {
                output += '\n';
                return;
            }

            const isBlock = blockTags.has(tag);
            if (isBlock && output.length > 0 && !output.endsWith('\n')) {
                output += '\n';
            }

            el.childNodes.forEach(walk);

            if (isBlock && !output.endsWith('\n')) {
                output += '\n';
            }
        };

        root.childNodes.forEach(walk);

        return output
            .replace(/\u200B/g, '')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trimEnd();
    },

    /**
     * Applies a wrapper element (created by the factory) to each paragraph-level
     * portion of the range separately, so that <p> block boundaries are never
     * partially selected — which would cause surroundContents to throw a
     * HierarchyRequestError.
     *
     * When the selection is entirely within one paragraph (or there are no <p>
     * elements in the editor at all) the wrapper is applied in a single operation,
     * exactly as before.  When the selection spans multiple paragraphs the range
     * is split into one sub-range per touched <p> and the wrapper is applied to
     * each sub-range independently.
     *
     * Returns the array of wrapper elements that were inserted (one per paragraph
     * touched by the selection, in document order).
     */
    _applyWrapperToRange(range: Range, editor: EditorElement, createWrapper: () => HTMLElement): HTMLElement[] {
        const wrappers: HTMLElement[] = [];

        const applyToSubRange = (subRange: Range): void => {
            if (subRange.collapsed) {
                return;
            }

            const wrapper = createWrapper();

            try {
                subRange.surroundContents(wrapper);
            } catch {
                const contents = subRange.extractContents();
                wrapper.appendChild(contents);
                subRange.insertNode(wrapper);
            }

            wrappers.push(wrapper);
        };

        // Walk up from a node to find the nearest <p> ancestor within the editor.
        const getContainingP = (node: Node): HTMLParagraphElement | null => {
            let n: Node | null = node;
            while (n && n !== editor) {
                if (n.nodeType === Node.ELEMENT_NODE && (n as HTMLElement).tagName === 'P') {
                    return n as HTMLParagraphElement;
                }
                n = n.parentNode;
            }
            return null;
        };

        const startP = getContainingP(range.startContainer);
        const endP = getContainingP(range.endContainer);

        if (startP === endP) {
            // Entire selection is within one <p> (or there are no <p> elements).
            applyToSubRange(range);
            return wrappers;
        }

        // Multi-paragraph selection: find all <p> elements touched by the range
        // and apply the wrapper to each paragraph's portion independently.
        //
        // compareBoundaryPoints semantics used below:
        //   END_TO_START  → compares range.end   to pRange.start  → > 0 means range ends after p starts
        //   START_TO_END  → compares range.start to pRange.end    → < 0 means range starts before p ends
        // Both true together = p is intersected by the range.
        const touchedPs = Array.from(editor.querySelectorAll('p')).filter((p) => {
            const pRange = document.createRange();
            pRange.selectNodeContents(p);
            return (
                range.compareBoundaryPoints(Range.END_TO_START, pRange) > 0 &&
                range.compareBoundaryPoints(Range.START_TO_END, pRange) < 0
            );
        });

        touchedPs.forEach((p, i) => {
            const subRange = document.createRange();

            if (i === 0) {
                // First paragraph: from the selection start to the end of this <p>.
                subRange.setStart(range.startContainer, range.startOffset);
                subRange.setEnd(p, p.childNodes.length);
            } else if (i === touchedPs.length - 1) {
                // Last paragraph: from the start of this <p> to the selection end.
                subRange.setStart(p, 0);
                subRange.setEnd(range.endContainer, range.endOffset);
            } else {
                // Middle paragraph: the entire content of this <p>.
                subRange.selectNodeContents(p);
            }

            applyToSubRange(subRange);
        });

        return wrappers;
    },
}

// Register for Blazor interop
if (typeof window !== 'undefined') {
    (window as any)['_zaa_services'] = (window as any)['_zaa_services'] || {};
    (window as any)['_zaa_services']['richTextEditor'] = RichTextEditor;
}

export default RichTextEditor;